import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  IconX,
  IconScale,
  IconDiamond,
  IconTag,
  IconCertificate,
  IconBarcode,
  IconFileText,
  IconPhoto,
  IconZoomIn,
  IconLoader2,
  IconCheck,
  IconReceiptTax,
  IconCalculator,
} from '@tabler/icons-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

export default function ConfirmationPembelian({
  isOpenConfPembelian,
  harga_beli,
  formData,
  closeModalConfPembelian,
  handleSubmitPembelian,
  selectImage = [],
  loading,
  namaKategori = '',
  namaJenis = '',
}) {
  function numberWithCommas(x) {
    if (x === null || x === undefined || isNaN(Number(x))) return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const beratNum = Number(formData?.berat) || 0;
  const hargaBeliNum = Number(harga_beli) || 0;
  const average = beratNum > 0 ? Math.round(hargaBeliNum / beratNum) : 0;

  const karatText = formData?.karat ? `${formData.karat} Karat` : '-';
  const kodeItemText = formData?.kode_item?.trim() || '';
  const keteranganText = formData?.keterangan?.trim() || '';
  const kondisiText = formData?.kondisi_barang || '';
  const suratText = formData?.surat || '';

  return (
    <Transition appear show={isOpenConfPembelian} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={loading ? () => {} : closeModalConfPembelian}>
        {/* Backdrop overlay halus */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 -translate-y-2"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 -translate-y-2"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white dark:bg-slate-900 text-left align-middle shadow-2xl transition-all border border-slate-200/80 dark:border-slate-800 flex flex-col max-h-[92vh]">
                {/* Aksen garis emas di puncak modal */}
                <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 shrink-0" />

                {/* Header Modal */}
                <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 shrink-0 bg-white dark:bg-slate-900">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-amber-500/10 dark:bg-amber-400/15 border border-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 shadow-xs">
                      <IconReceiptTax size={22} stroke={2.2} />
                    </div>
                    <div className="min-w-0">
                      <Dialog.Title as="h3" className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-tight">
                        Konfirmasi Simpan Pembelian
                      </Dialog.Title>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                        Periksa rincian barang dan nominal transaksi sebelum disimpan
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={closeModalConfPembelian}
                    disabled={loading}
                    className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors disabled:opacity-40 cursor-pointer"
                  >
                    <IconX size={18} />
                  </button>
                </div>

                {/* Body Modal (Scrollable) */}
                <div className="p-5 sm:p-6 overflow-y-auto space-y-4 custom-scrollbar">
                  {/* Hero Card: Total Nilai Transaksi & Rata-rata per Gram */}
                  <div className="rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-amber-500/12 via-amber-500/6 to-yellow-500/10 dark:from-amber-950/40 dark:via-slate-800/70 dark:to-slate-800/40 border border-amber-500/25 shadow-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                          Total Harga Beli
                        </span>
                        <div className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 tracking-tight mt-0.5">
                          Rp {numberWithCommas(harga_beli)}
                        </div>
                      </div>

                      {/* Pill Badge Rata-rata */}
                      <div className="px-3 py-1.5 rounded-xl bg-amber-500/15 dark:bg-amber-400/15 border border-amber-500/20 text-amber-800 dark:text-amber-300 flex items-center gap-1.5 text-xs font-bold shrink-0">
                        <IconCalculator size={15} stroke={2} className="text-amber-600 dark:text-amber-400" />
                        <span>Rp {numberWithCommas(average)} / gr</span>
                      </div>
                    </div>
                  </div>

                  {/* Grid 4 Spesifikasi Utama Barang */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* 1. Berat Barang */}
                    <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs font-semibold mb-1">
                        <IconScale size={16} className="text-amber-600 dark:text-amber-400" />
                        <span>Berat Barang</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        <span className="text-amber-600 dark:text-amber-400">{formData?.berat || '0'}</span>
                        <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-1">gram</span>
                      </div>
                    </div>

                    {/* 2. Kadar / Karat */}
                    <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs font-semibold mb-1">
                        <IconDiamond size={16} className="text-amber-600 dark:text-amber-400" />
                        <span>Kadar / Karat</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        {karatText}
                      </div>
                    </div>

                    {/* 3. Jenis & Kategori */}
                    <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs font-semibold mb-1">
                        <IconTag size={16} className="text-amber-600 dark:text-amber-400" />
                        <span>Jenis Barang</span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate">
                          {namaJenis || 'Jenis Tidak Dipilih'}
                        </div>
                        {namaKategori && (
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                            {namaKategori}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 4. Kondisi & Surat */}
                    <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800 flex flex-col justify-between">
                      <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs font-semibold mb-1.5">
                        <IconCertificate size={16} className="text-amber-600 dark:text-amber-400" />
                        <span>Surat & Kondisi</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {suratText === 'ada' ? (
                          <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold border border-emerald-500/20">
                            Ada Surat
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-md bg-slate-500/10 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400 text-[11px] font-semibold border border-slate-500/20">
                            Tanpa Surat
                          </span>
                        )}

                        {kondisiText && (
                          <span
                            className={`px-2 py-0.5 rounded-md text-[11px] font-bold border capitalize ${
                              kondisiText.toLowerCase() === 'mulus'
                                ? 'bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/20'
                                : 'bg-rose-500/10 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/20'
                            }`}
                          >
                            {kondisiText}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Kode Item (Jika Ada) */}
                  {kodeItemText && (
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-800 flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 font-medium">
                        <IconBarcode size={17} className="text-slate-400" />
                        <span>Kode Barang:</span>
                      </div>
                      <span className="font-mono font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-900 px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700">
                        {kodeItemText}
                      </span>
                    </div>
                  )}

                  {/* Keterangan / Catatan Tambahan (Jika Ada) */}
                  {keteranganText && (
                    <div className="p-3 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/40 text-xs">
                      <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300 font-semibold mb-1">
                        <IconFileText size={15} />
                        <span>Catatan Transaksi:</span>
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 italic pl-5">
                        "{keteranganText}"
                      </p>
                    </div>
                  )}

                  {/* Section Foto Surat / Bukti Fisik */}
                  {selectImage && selectImage.length > 0 && (
                    <div className="pt-2">
                      <div className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300">
                          <IconPhoto size={16} className="text-amber-600 dark:text-amber-400" />
                          <span>Lampiran Foto / Surat</span>
                          <span className="px-1.5 py-0.2 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-[10px]">
                            {selectImage.length}
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1">
                          <IconZoomIn size={13} />
                          Klik untuk memperbesar
                        </span>
                      </div>

                      <PhotoProvider>
                        <div className="flex items-center gap-3 overflow-x-auto pb-1 custom-scrollbar">
                          {selectImage.map((image, index) => (
                            <PhotoView key={index} src={image}>
                              <div className="relative group cursor-pointer shrink-0">
                                <img
                                  className="h-20 w-20 rounded-2xl object-cover border-2 border-slate-200 dark:border-slate-700 group-hover:border-amber-500 transition-all shadow-xs group-hover:shadow-md"
                                  src={image}
                                  alt={`Lampiran ${index + 1}`}
                                />
                                <div className="absolute inset-0 rounded-2xl bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                                  <IconZoomIn size={20} />
                                </div>
                              </div>
                            </PhotoView>
                          ))}
                        </div>
                      </PhotoProvider>
                    </div>
                  )}
                </div>

                {/* Footer Modal: Action Buttons */}
                <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={closeModalConfPembelian}
                    disabled={loading}
                    className="px-4.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-sm transition-all cursor-pointer disabled:opacity-50"
                  >
                    Batal
                  </button>

                  <button
                    type="button"
                    onClick={handleSubmitPembelian}
                    disabled={loading}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 active:scale-[0.98] text-white font-bold text-sm shadow-md shadow-amber-500/25 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <IconLoader2 size={18} className="animate-spin" />
                        <span>Menyimpan Transaksi...</span>
                      </>
                    ) : (
                      <>
                        <IconCheck size={18} stroke={2.5} />
                        <span>Simpan Pembelian</span>
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
